import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';

const AdminStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const BASEURL = process.env.REACT_APP_API_URL

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASEURL}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }

      const data = await response.json();
      setStats(data.stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error: {error}
        </Alert>
        <button onClick={fetchStats} style={{ padding: '8px 16px' }}>
          🔄 Refresh
        </button>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box>
        <Typography color="error" variant="h6">
          No statistics available
        </Typography>
      </Box>
    );
  }

  const StatCard = ({ title, value, icon, color = 'primary', subtitle = null }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color={color} fontSize="2rem">
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography 
          variant="h4" 
          component="h2"
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: 700
          }}
        >
          System Statistics
        </Typography>
        <Tooltip title="Refresh">
          <button 
            onClick={fetchStats} 
            style={{ 
              padding: '0.75rem 1.5rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(23, 162, 184, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(23, 162, 184, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(23, 162, 184, 0.3)';
            }}
          >
            🔄 Refresh
          </button>
        </Tooltip>
      </Box>

      <Grid container spacing={3}>
        {/* Users Statistics */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats?.users?.total || 0}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Total Users
                  </Typography>
                </Box>
                <Box sx={{ fontSize: '3rem', opacity: 0.8 }}>👥</Box>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${stats?.users?.admins || 0} Admins`} 
                  size="small" 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 500
                  }} 
                />
                <Chip 
                  label={`${stats?.users?.exam_managers || 0} Managers`} 
                  size="small" 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 500
                  }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Students Statistics */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(40, 167, 69, 0.4)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats?.students || 0}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Total Students
                  </Typography>
                </Box>
                <Box sx={{ fontSize: '3rem', opacity: 0.8 }}>🎓</Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Exams Statistics */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(255, 193, 7, 0.3)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(255, 193, 7, 0.4)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats?.exams || 0}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Total Exams
                  </Typography>
                </Box>
                <Box sx={{ fontSize: '3rem', opacity: 0.8 }}>📝</Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Rooms Statistics */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(23, 162, 184, 0.3)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(23, 162, 184, 0.4)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats?.rooms || 0}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Total Rooms
                  </Typography>
                </Box>
                <Box sx={{ fontSize: '3rem', opacity: 0.8 }}>🏠</Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Modules Statistics */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #e83e8c 0%, #fd7e14 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(232, 62, 140, 0.3)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(232, 62, 140, 0.4)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats?.modules || 0}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Total Modules
                  </Typography>
                </Box>
                <Box sx={{ fontSize: '3rem', opacity: 0.8 }}>📚</Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Levels Statistics */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #6f42c1 0%, #e83e8c 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(111, 66, 193, 0.3)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(111, 66, 193, 0.4)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats?.levels || 0}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Total Levels
                  </Typography>
                </Box>
                <Box sx={{ fontSize: '3rem', opacity: 0.8 }}>📊</Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {!stats && (
        <Box textAlign="center" py={6}>
          <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
            📊 Loading statistics...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AdminStats; 